import {
  ORGS_THAT_PERSON_BELONGS_TO_QUERY,
  PERSON_BY_EMAIL_QUERY,
} from '@src/gql/person-gql';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';
import type { DatoContext } from './types';
import type { UserResource } from '@clerk/types';
import type { Person, Showcase } from '@src/types.ts/story';
import type { Logger } from '@src/utility/Logger';
import type { User } from 'datocms-plugin-sdk';

// Not to be confused with DatoUser vs ClerkUser. This is for Person dato model
export class DatoPersonStore {
  // Core state
  person: Person | null = null;
  term: string = '';
  // That the user is part of
  organizations: Showcase[] = [];
  selectedOrganizationId: string | null = null;

  private _rootStore: RootStore;
  private _logger: Logger;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    // Set to null by default
    this.person = null;
    this._logger = LogManager.getLogger('dato-person-store');
    makeAutoObservable(this);
  }

  /**
   * Initialize the DatoPersonStore from clerk user data
   * @param clerkUser Currently logged in user from Clerk
   */
  public async initializePersonFromClerkUser(clerkUser: UserResource) {
    if (!this.canInitializeFromClerkUser(clerkUser)) {
      this._logger.warn(
        'Clerk user is not valid for initialization',
        clerkUser.id,
      );
      return;
    }
    await this.initializePersonByEmail(this.getClerkUserEmail(clerkUser));
  }

  public async initializePersonFromDatoUser(
    datoUser: DatoContext['currentUser'],
  ) {
    if (!this.canInitializeFromDatoUser(datoUser)) {
      this._logger.warn(
        'Dato user is not valid for initialization',
        datoUser.id,
      );
      return;
    }
    const email = this.getDatoUserEmail(datoUser);
    if (email) {
      await this.initializePersonByEmail(email);
    } else {
      // For Dato users without email, use placeholder email
      const placeholderEmail = `dato-user-${datoUser.id}@placeholder.com`;
      await this.initializePersonByEmail(placeholderEmail);
    }
  }

  public canSwitchBetweenOrganizations(): boolean {
    return this.organizations.length > 1 && this.person != null;
  }

  public get selectedOrganization(): Showcase | null {
    if (!this.selectedOrganizationId) {
      return null;
    }
    return (
      this.organizations.find(org => org.id === this.selectedOrganizationId) ||
      null
    );
  }

  public set selectedOrganization(org: Showcase | null) {
    if (org && this.organizations.some(o => o.id === org.id)) {
      this.selectedOrganizationId = org.id;
      this._logger.info('Selected organization', org.id);
    }
  }

  public set searchTerm(term: string) {
    this.term = term;
  }

  public get organizationsFiltered(): Showcase[] {
    if (!this.term) {
      return this.organizations;
    }
    const lowerTerm = this.term.toLowerCase();
    return this.organizations.filter(org =>
      org.title.toLowerCase().includes(lowerTerm),
    );
  }

  public async initializePersonByEmail(
    email: string,
    forceRefresh: boolean = false,
  ): Promise<void> {
    if (this.person?.email === email && !forceRefresh) {
      this._logger.info('Person already initialized for email', email);
      return;
    }
    // Load person
    this.person = await this.fetchPersonByEmail(email);
    if (this.person) {
      this._logger.info('Person loaded', {
        id: this.person.id,
        email: this.person.email,
      });
    } else {
      this._logger.warn(
        'No person found for user',
        email,
        '- creating new person',
      );
      // Create a new person record
      this.person = await this.createPerson(email);
      if (!this.person) {
        this._logger.error('Failed to create person for email', email);
        return;
      }
      this._logger.info('Person created', { id: this.person.id, email });
    }
    this.person.email = email;
    // Load organizations
    this.organizations = await this.fetchPersonOrganizations();
    if (this.organizations.length > 0) {
      this._logger.info('Organizations loaded for person', this.organizations);
    } else {
      this._logger.warn('No organizations found for person', this.person.id);
      return;
    }
  }

  // Used as a fallback when no org is loaded in VideoCreatorStore
  // and no org is selected in DatoPersonStore
  public getFallbackOrganization(): Showcase | null {
    if (this.organizations.length > 0) {
      return this.organizations[0];
    }
    return null;
  }

  private canInitializeFromClerkUser(clerkUser: UserResource): boolean {
    const emailAddress = this.getClerkUserEmail(clerkUser);
    if (!emailAddress) {
      this._logger.warn('Clerk user has no email addresses', clerkUser.id);
      return false;
    }
    // Uncomment this block if you want to skip initialization for internal users
    //if (emailAddress.endsWith('@tryarbor.com')) {
    //  this._logger.info('Clerk user is an internal user, skipping initialization', clerkUser.id);
    //  return false;
    //}
    return true;
  }

  private canInitializeFromDatoUser(
    datoUser: DatoContext['currentUser'],
  ): boolean {
    // We can initialize even without email, using user ID
    if (!datoUser.id) {
      this._logger.warn('Dato user has no ID', datoUser);
      return false;
    }
    return true;
  }

  private async fetchPersonByEmail(email: string): Promise<Person | null> {
    try {
      const personResponse = (await this.gqlClient.request(
        PERSON_BY_EMAIL_QUERY,
        {
          email,
        },
      )) as { allPeople: (Person & { id: string })[] };

      if (personResponse.allPeople.length > 0) {
        return personResponse.allPeople[0];
      } else {
        return null;
      }
    } catch (error) {
      this._logger.error('Error fetching person by email', error);
      return null;
    }
  }

  private async fetchPersonOrganizations(): Promise<Showcase[]> {
    if (!this.person) {
      this._logger.error('No person loaded, cannot fetch organizations');
      return [];
    }
    try {
      const organizationsResponse = (await this.gqlClient.request(
        ORGS_THAT_PERSON_BELONGS_TO_QUERY,
        { personId: this.person.id },
      )) as { allShowcases: Showcase[] };
      return organizationsResponse.allShowcases || [];
    } catch (error) {
      this._logger.error('Error fetching organizations for person', error);
      return [];
    }
  }

  private async createPerson(email: string): Promise<Person | null> {
    try {
      const datoClient = this._rootStore.datoClientStore.datoClient;

      // Get the person item type
      const personItemType = await datoClient.itemTypes.find('person');

      // Generate a default name from email (e.g., "john.doe@example.com" -> "John Doe")
      const defaultName = email
        .split('@')[0]
        .split(/[._-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Create the person record
      const createdPerson = await datoClient.items.create({
        item_type: personItemType,
        name: defaultName,
        email: email,
      });

      return {
        id: createdPerson.id,
        name: createdPerson.name as string,
        email: createdPerson.email as string,
        title: createdPerson.title as string | undefined,
        phone: createdPerson.phone as string | undefined,
        user: createdPerson.user as Person['user'],
        favoritePrompts: createdPerson.favorite_prompts,
      };
    } catch (error) {
      this._logger.error('Error creating person', error);
      return null;
    }
  }

  private get gqlClient() {
    return this._rootStore.datoClientStore.gqlClient;
  }

  private getDatoUserEmail(datoUser: DatoContext['currentUser']): string {
    // Try multiple ways to get the email
    if ((datoUser as User).attributes?.email) {
      return (datoUser as User).attributes.email;
    }
    if ((datoUser as any).email) {
      return (datoUser as any).email;
    }
    // Log the structure to debug
    this._logger.info('Dato user structure:', datoUser);
    return '';
  }

  private getClerkUserEmail(clerkUser: UserResource): string {
    return clerkUser.emailAddresses[0]?.emailAddress || '';
  }

  /**
   * Initialize person by user ID when email is not available
   * @param userId The user ID from identity system
   * @param source The source system ('dato' or 'clerk')
   */
  public async initializePersonByUserId(
    userId: string,
    source: 'dato' | 'clerk',
  ): Promise<void> {
    // For now, we'll use the placeholder email approach since we can't query JSON fields easily
    // This method might not be needed anymore since we're using placeholder emails
    const placeholderEmail = `${source}-user-${userId}@placeholder.com`;
    await this.initializePersonByEmail(placeholderEmail);
  }
}
